import type { ChargingSessionType } from '@/api/schema';
import { CardsNumberCell } from '@/components/CardsNumberCell';
import Money from '@/components/Money';
import { formatDateLocalized, formatEnergyValue, t } from '@/lib/utils';

export type SummaryTabProps = {
    state?: ChargingSessionType;
    locale: string;
    currency: string;
};

export const SummaryTab = ({ state, locale, currency }: SummaryTabProps) => (
    <>
        <div className='align-items-center divider-y display-flex flex-column gap-0'>
            {state?.startedAt && (
                <div className='width-100pct'>
                    <div className='padding-y-15 display-flex justify-content-between'>
                        <span className='text-color-gray'>{t('chargingSessions.sessionStart')}</span>
                        <span>{formatDateLocalized(new Date(state.startedAt), locale, true)}</span>
                    </div>
                </div>
            )}
            {state?.endedAt && (
                <div className='width-100pct'>
                    <div className='padding-y-15 display-flex justify-content-between'>
                        <span className='text-color-gray'>{t('chargingSessions.sessionEnd')}</span>
                        <span>{formatDateLocalized(new Date(state.endedAt), locale, true)}</span>
                    </div>
                </div>
            )}
            <div className='width-100pct'>
                <div className='padding-y-15 display-flex justify-content-between'>
                    <span className='text-color-gray'>{t('chargingSessions.duration')}</span>
                    <span>{state?.duration}</span>
                </div>
            </div>
            <div className='width-100pct'>
                <div className='padding-y-15 display-flex justify-content-between'>
                    <span className='text-color-gray'>{t('chargingSessions.chargingCardUsed')}</span>
                    <CardsNumberCell id={state?.token?.number || ''} />
                </div>
            </div>
            <div className='width-100pct'>
                <div className='padding-y-15 display-flex justify-content-between'>
                    <span className='text-color-gray'>{t('chargingSessions.chargingCardAlias')}</span>
                    <span className={state?.token?.alias ? 'text-color-black' : 'text-color-gray'}>
                        {state?.token?.alias ? state?.token?.alias : 'No alias added'}
                    </span>
                </div>
            </div>
            <div className='width-100pct'>
                <div className='padding-y-15 display-flex justify-content-between'>
                    <span className='text-color-gray'>{t('chargingSessions.plugType')}</span>
                    <span>{state?.chargingPoint?.plugType}</span>
                </div>
            </div>
        </div>
        <div className='display-flex flex-column gap-0 margin-bottom-15'>
            <div className='width-100pct margin-bottom-0 padding-y-15 text-bold text-size-16'>
                {t('chargingSessions.invoicing')}
            </div>
            {state?.invoiceNumber ? (
                <div className='align-items-center divider-y display-flex flex-column gap-0 border border-top-only border-width-1 border-color-light'>
                    <div className='width-100pct'>
                        <div className='padding-y-15 display-flex justify-content-between'>
                            <span className='text-color-gray'>{t('chargingSessions.invoiceNumber')}</span>
                            <span>{state?.invoiceNumber}</span>
                        </div>
                    </div>
                    <div className='width-100pct'>
                        <div className='padding-y-15 display-flex justify-content-between'>
                            <span className='text-color-gray'>{t('chargingSessions.invoiceDate')}</span>
                            <span>
                                {state?.invoicedAt
                                    ? formatDateLocalized(new Date(state.invoicedAt), locale, false)
                                    : ''}
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='alert alert-warning padding-10 width-100pct'>
                    <div className='display-flex gap-10'>
                        <span className='text-color-warning text-size-h2 rioglyph rioglyph-time' />
                        <div>
                            <strong className='text-size-16'>{t('chargingSessions.pendingInvoice')}</strong>
                            <div className='margin-y-5'>{t('chargingSessions.pendingInvoice.description')}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        <div className='padding-20 display-flex width-100pct gap-20 rounded border'>
            <div className='position-relative display-flex flex-1-0 flex-column align-items-center'>
                <span>{t('general.energyCharged')}:</span>
                <span className='text-bold text-size-h3'>
                    {formatEnergyValue(state?.energy ?? 0, locale).value}
                    <span className='text-normal text-size-16 margin-left-5'>kWh</span>
                </span>
            </div>
            <div className='border border-left-only border-width-2' />
            <div className='display-flex flex-1-0 flex-column align-items-center'>
                <span>{t('general.costNet')}:</span>
                <span className='text-bold text-size-h3'>
                    {state?.totalCost ? <Money currency={currency} value={state.totalCost} /> : 'N/A'}
                </span>
            </div>
        </div>
    </>
);
